import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import style from "./NewOrderSummary.module.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { BrowserDetectContext } from "providers/BrowserEventProvider";
import { RegionContext } from "providers/RegionProvider";
import { AuthContext } from "providers/AuthProvider";
import { storeRequester } from "App";
import { Cookie, deleteCookie, getCookie } from "shared/StoreRequester";
import Loader from "components/inputs/image/Loader";
import ShippingSection from "./orderSummary/ShippingSection";
import ProductSection from "./orderSummary/ProductSection";
import PaymentSection from "./orderSummary/PaymentSection";
import OrderSummaryPanel from "./orderSummary/OrderSummaryPanel";
import P from "components/P";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import Dummy from "components/Dummy";
import Center from "layouts/wrapper/Center";
import VerticalFlex from "layouts/flex/VerticalFlex";
import FlexChild from "layouts/flex/FlexChild";
import TaxInvoiceScetion from "./orderSummary/TaxInvoiceScetion";

const NewOrderSummary = () => {
  // Contexts
  const { t } = useTranslation();
  const { userName } = useContext(AuthContext);
  const { isMobile, languageCode } = useContext(BrowserDetectContext);
  const { regionId } = useContext(RegionContext);
  const navigate = useNavigate();

  // Refs
  const shippingInfoRef = useRef();
  // const messageRef = useRef();

  // Basic State
  const [cartItems, setCartItems] = useState([]);
  const [productTotalPrice, setProductTotalPrice] = useState(0);
  const [VAT, setVAT] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [shippingAddress, setShippingAddress] = useState([]);
  const [selectAddress, setSelectAddress] = useState({
    name: "",
    first_name: "",
    last_name: "",
    phone: "",
    company: "",
    address_1: "",
    address_2: "",
    city: "",
    country_code: "",
    province: "",
    postal_code: "",
    name: "",
  });
  const [selectAddressId, setSelectAddressId] = useState(null);
  const [agree, setAgree] = useState(false);
  const [add, setAdd] = useState(false);

  // Event States
  const [eventResult, setEventResult] = useState([]);
  const [eventItemTotalQuantity, setEventItemTotalQuantity] = useState([]);
  const [usePoint, setUsePoint] = useState(0);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipingOption, setSelectedShippingOption] = useState();
  const CART_STATUS = {
    INITIAL: "initial",
    LOADING: "loading",
    EMPTY: "empty",
    LOADED: "loaded",
  };
  const [cartStatus, setCartStatus] = useState(CART_STATUS.INITIAL);
  const [pageLoading, setPageLoading] = useState(false);
  const [isOrderValid, setIsOrderValid] = useState(true);

  // Fetch shipping options
  const fetchShippingOptions = useCallback(() => {
    storeRequester.getShippingOptions({}, ({ shipping_options }) =>
      setShippingOptions(shipping_options)
    );
  }, []);

  // const handleMessageChange = useCallback((value) => {
  //   setMessage(value);
  //   if (messageRef.current) {
  //     messageRef.current.value = value;
  //   }
  // }, []);

  const handleMessageChange = useCallback((newMessage) => {
    setMessage(newMessage);
  }, []);

  // Cart Management
  const fetchCart = useCallback(async () => {
    try {
      if (cartStatus === CART_STATUS.INITIAL) {
        setCartStatus(CART_STATUS.LOADING);
      }
      const { cart } = await storeRequester.getMyCart(regionId);
      // 재고 체크 및 부족 아이템 처리
      const itemsToRemove = cart.items.filter(
        item => item.quantity > item.variant?.inventory_quantity
      );
      
      // 재고 부족 아이템이 있다면 삭제 처리
      if (itemsToRemove.length > 0) {
        // 각 아이템에 대해 삭제 요청 및 알림
        for (const item of itemsToRemove) {
          await storeRequester.removeItem(cart.id, item.id);
          toast.warning(`${item.title} 상품이 재고 부족으로 삭제되었습니다.`, {
            autoClose: 2000,
            position: toast.POSITION.BOTTOM_CENTER,
          });
        }
        // 장바구니 다시 조회
        const { cart: updatedCart } = await storeRequester.getMyCart(regionId);
        cart.items = updatedCart.items;
      }

      const hasChanged =
        JSON.stringify(cart.items) !== JSON.stringify(cartItems);

      if (hasChanged) {
        setCartItems(cart.items);
      }
      setCartStatus(cart.items.length ? CART_STATUS.LOADED : CART_STATUS.EMPTY);

      return cart;
    } catch (error) {
      setCartStatus(CART_STATUS.EMPTY);
      toast.error("장바구니 정보를 불러오는데 실패했습니다.", {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
    }
  }, [regionId, cartStatus]);



  // 이벤트 감지 및 계산
  const calculateEventQuantities = useCallback((items, events) => {
    if (!events?.length || !items?.length) return;

    const eventCalculations = events.map((event) => {
      const { id, products, N, M, cross } = event;

      // Event items 필터링 - variant.product.id 사용
      const eventItems = items.filter((item) =>
        products.some((p) => p.id === item.variant?.product?.id)
      );

      // Regular items와 free items 분리
      const regularItems = eventItems.filter((item) => !item.free);
      const freeItems = eventItems.filter((item) => item.free);

      if (cross) {
        // Cross event 계산
        const totalQuantity = regularItems.reduce(
          (sum, item) => sum + item.quantity,
          0
        );
        const allowedFreeItems = Math.floor(totalQuantity / N) * M;
        const currentFreeItems = freeItems.reduce(
          (sum, item) => sum + item.quantity,
          0
        );

        return {
          eventId: id,
          cross: true,
          N,
          M,
          totalQuantity,
          allowedFreeItems,
          currentFreeItems,
          remainingFreeItems: allowedFreeItems - currentFreeItems,
          products, // 전체 products 정보 포함
          regularItems,
          freeItems,
        };
      } else {
        const variantCalculations = regularItems.reduce((acc, item) => {
          const variantQuantity = item.quantity;
          const allowedFreeItems = Math.floor(variantQuantity / N) * M;
          const variantFreeItems = freeItems.filter(
            (f) => f.variant_id === item.variant_id
          );
          const currentFreeItems = variantFreeItems.reduce(
            (sum, f) => sum + f.quantity,
            0
          );

          acc[item.variant_id] = {
            variantId: item.variant_id,
            productId: item.variant.product_id,
            quantity: variantQuantity,
            allowedFreeItems,
            currentFreeItems,
            remainingFreeItems: allowedFreeItems - currentFreeItems,
          };
          return acc;
        }, {});

        const totalQuantity = regularItems.reduce(
          (sum, item) => sum + item.quantity,
          0
        );
        const totalAllowedFree = Object.values(variantCalculations).reduce(
          (sum, calc) => sum + calc.allowedFreeItems,
          0
        );
        const totalCurrentFree = Object.values(variantCalculations).reduce(
          (sum, calc) => sum + calc.currentFreeItems,
          0
        );

        return {
          eventId: id,
          cross: false,
          N,
          M,
          totalQuantity,
          allowedFreeItems: totalAllowedFree,
          currentFreeItems: totalCurrentFree,
          remainingFreeItems: totalAllowedFree - totalCurrentFree,
          products,
          regularItems,
          freeItems,
          variantCalculations,
        };
      }
    });

    setEventItemTotalQuantity(eventCalculations);
  }, []);

  const handleValidateOrder = (isValid) => {
    setIsOrderValid(isValid);
  };

  // Payment Processing
  const handlePaymentCompleted = async () => {
    const requiredFields = [
      "first_name",
      "phone",
      "address_1",
      "address_2",
      "postal_code",
    ];
    const isEmpty = requiredFields.some(
      (field) => !selectAddress[field]?.trim()
    );

    if (isEmpty) {
      toast.error(t("배송지 정보를 모두 입력해주세요"), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    const cartId = getCookie(Cookie.Cart);
    if (!cartId) return;
    const { cart } = await storeRequester.getMyCart(regionId);
    if (cart.id !== cartId) {
      window.location.reload();
      return;
    }
    try {
      setPageLoading(true);
      // const message = selectAddress.message
      //   ? selectAddress.message
      //   : messageRef.current?.value;
      // const messageToSend = message || messageRef.current?.value;
      const messageToSend = message

      storeRequester
        .updateCart(cartId, {
          shipping_address: {
            // name: selectAddress.name,
            first_name: selectAddress.first_name,
            last_name: selectAddress.last_name,
            phone: selectAddress.phone,
            company: selectAddress.company,
            address_1: selectAddress.address_1,
            address_2: selectAddress.address_2,
            city: selectAddress.city,
            country_code: selectAddress.country_code,
            province: selectAddress.province,
            postal_code: selectAddress.postal_code,
            metadata: selectAddress.metadata,
          },
        })
        .then(() => {
          storeRequester.createPaymentSession(cartId).then((s) => {
            storeRequester
              .addShipoingMethod(cartId, {
                option_id: selectedShipingOption?.id,
              })
              .then(() => {
                storeRequester.placeOrder(cartId).then(({ data }) => {
                  storeRequester
                    .updateOrder(data.id, {
                      message: messageToSend,
                      point: usePoint,
                    })
                    .then((result) => {
                      setPageLoading(false);
                      deleteCookie(Cookie.Cart);
                      storeRequester.getMyCart(regionId);
                      navigate("/shopping/orderComplete", {
                        state: {
                          data: productTotalPrice + VAT,
                          selectedShipingOption: selectedShipingOption,
                          usePoint: usePoint,
                        },
                      });
                    });
                });
              });
          });
        })
        .catch((error) => {
          setPageLoading(false);
          toast.error("주문 처리 중 오류가 발생했습니다.", {
            autoClose: 1000,
            position: toast.POSITION.BOTTOM_CENTER,
          });
        });
    } catch (error) {
      setPageLoading(false);
      toast.error("주문 처리 중 오류가 발생했습니다.", {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
    }
  };

  const handleOrderButtonClick = () => {
    if (!agree || !currentCustomer) {
      toast.error(t("agreeToTheTerms"), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    if (!isOrderValid) {
      toast.error(
        "선택 가능한 사은품이 남아있습니다. 모든 사은품을 선택해주세요.",
        {
          autoClose: 2000,
          position: toast.POSITION.BOTTOM_CENTER,
        }
      );
      return;
    }

    handlePaymentCompleted();
  };

  const handleCheckGroupCallback = useCallback((checkedIndex) => {
    const hasRequiredChecks = checkedIndex.length >= 2;
    setAgree(hasRequiredChecks);
  }, []);

  const fetchCustomer = async () => {
    if (userName) {
      const { customer } = await storeRequester.getCurrentCustomer({
        expand: "shipping_addresses",
      });
      setCurrentCustomer(customer);
      setShippingAddress(customer?.shipping_addresses || []);
      return customer?.shipping_addresses || [];
    } else {
      setCurrentCustomer(null);
      setShippingAddress([]);
    }
  };

  // 초기화를 위한 useEffect
  useEffect(() => {
    const initializeCart = async () => {
      try {
        await fetchCart();
        fetchShippingOptions();
      } catch (error) {}
    };

    initializeCart();
  }, []);

  // cartItems 변경에 따른 계산을 위한 useEffect
  useEffect(() => {
    const handleCartUpdates = async () => {
      if (!cartItems?.length) return;

      // 금액 계산
      const { total, VAT } = cartItems.reduce(
        (sum, item) => ({
          total: sum.total + item.subtotal,
          VAT: sum.VAT + item.tax_total,
        }),
        { total: 0, VAT: 0 }
      );
      setProductTotalPrice(total);
      setVAT(VAT);

      // 이벤트 계산
      try {
        const { events } = await storeRequester.getEvents({
          expand: "products.variants.prices",
          products: cartItems
            .map((item) => item.variant?.product?.id)
            .filter(Boolean),
        });

        if (events?.length) {
          // 현재 시간과 비교하여 유효한 이벤트만 필터링
          const currentDate = new Date();

          // 1. 유효한 이벤트 필터링
          const activeEvents = events.filter((event) => {
            const endDate = new Date(event.end_date);
            const startDate = new Date(event.start_date);
            return endDate > currentDate && startDate <= currentDate;
          });

          // 2. endDate로 내림차순 정렬
          const sortedByEndDate = activeEvents.sort((a, b) => {
            const endDateDiff = new Date(b.end_date) - new Date(a.end_date);
            if (endDateDiff === 0) {
              // endDate가 같은 경우 updated_at으로 정렬
              return new Date(b.updated_at) - new Date(a.updated_at);
            }
            return endDateDiff;
          });

          // 3. 같은 상품 그룹에 대해 하나의 이벤트만 선택
          const groupedEvents = [];
          const processedProductIds = new Set();

          sortedByEndDate.forEach((event) => {
            // 이벤트의 상품 ID들
            const eventProductIds = event.products.map((p) => p.id);

            // 이미 처리된 상품과 겹치는지 확인
            const isOverlapping = eventProductIds.some((id) =>
              processedProductIds.has(id)
            );

            if (!isOverlapping) {
              groupedEvents.push(event);
              eventProductIds.forEach((id) => processedProductIds.add(id));
            }
          });

          setEventResult(groupedEvents);
          calculateEventQuantities(cartItems, groupedEvents);
        }
      } catch (error) {}
    };

    handleCartUpdates();
  }, [cartItems]);

  // shipping options 및 금액에 따른 배송비 설정
  useEffect(() => {
    if (cartItems?.length > 0 && shippingOptions?.length > 0) {
      const total = cartItems.reduce((sum, item) => sum + item.subtotal, 0);

      const newSelectedOption =
        total >= 50000
          ? shippingOptions.find((option) => option.price_incl_tax === 0)
          : shippingOptions.find((option) => option.price_incl_tax !== 0);

      if (newSelectedOption) {
        setSelectedShippingOption(newSelectedOption);
      }
    }
  }, [cartItems, shippingOptions]);

  // customer 관련 useEffect
  useEffect(() => {
    fetchCustomer();
  }, [userName, add]);

  useEffect(() => {
    if (currentCustomer && getCookie(Cookie.Cart)) {
      storeRequester.updateCart(getCookie(Cookie.Cart), {
        customer_id: currentCustomer.id,
      });
    }
  }, [currentCustomer]);

  useEffect(() => {
    if (shippingAddress.length > 0 && selectAddressId) {
      const selectedAddr = shippingAddress.find(
        (addr) => addr.id === selectAddressId
      );
      if (selectedAddr) {
        setSelectAddress((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(selectedAddr).filter(([key]) => key in prev)
          ),
        }));
      }
    }
  }, [shippingAddress, selectAddressId]);

  const rowIndex = [
    {
      id: 1,
      accent: true,
      text: t("termsOfUseAgree"),
      header: t("termsOfUse"),
    },
    {
      id: 2,
      accent: true,
      privacy: true,
      text: t("privacyPolicyAgree"),
      header: t("privacyPolicy") + t("useToInfo"),
    },
  ];
  if (pageLoading) return <Loader />;
  if (
    cartStatus === CART_STATUS.INITIAL ||
    cartStatus === CART_STATUS.LOADING
  ) {
    return <Loader />;
  }
  if (cartStatus === CART_STATUS.EMPTY) {
    return (
      <VerticalFlex height={isMobile ? "auto" : "63vh"}>
        <FlexChild>
          <Dummy height={isMobile ? "100px" : "300px"} />
          <Center height={"100%"}>
            <div className="text-center">
              <FontAwesomeIcon
                icon={faShoppingCart}
                size="8x"
                color="var(--main-color)"
              />
              <Dummy height={"30px"} />
              <P color={"#aaa"} weight={700} size={32}>
                장바구니가 비었습니다.
              </P>
            </div>
          </Center>
          <Dummy height={isMobile ? "100px" : "30px"} />
        </FlexChild>
      </VerticalFlex>
    );
  }
  return (
    <div className={style.container}>
      <div className={style.mainContent}>
        <ProductSection
          isMobile={isMobile}
          cartItems={cartItems}
          setCartItems={setCartItems}
          fetchMedusaCart={fetchCart}
          eventResult={eventResult}
          eventItemTotalQuantity={eventItemTotalQuantity}
          onValidateOrder={handleValidateOrder}
        />
        <ShippingSection
          languageCode={languageCode}
          shippingInfoRef={shippingInfoRef}
          // messageRef={messageRef}
          message={message}  // messageRef 대신 직접 message 값을 전달
          onMessageChange={handleMessageChange}
          shippingAddress={shippingAddress}
          setAdd={setAdd}
          selectAddressId={selectAddressId}
          setSelectAddressId={setSelectAddressId}
          setSelectAddress={setSelectAddress}
          fetchCustomer={fetchCustomer}
        />
        <PaymentSection user={currentCustomer} isMobile={isMobile} />
        {/* <input type="hidden" ref={messageRef} user={currentCustomer} /> */}
        {/* <TaxInvoiceScetion /> */}
        <OrderSummaryPanel
          isMobile={isMobile}
          productTotalPrice={productTotalPrice}
          VAT={VAT}
          onOrderButtonClick={handleOrderButtonClick}
          onCheckGroupCallback={handleCheckGroupCallback}
          rowIndex={rowIndex}
          agree={agree}
          point={currentCustomer?.point || 0}
          usePoint={usePoint}
          setUsePoint={setUsePoint}
          shippingOption={selectedShipingOption}
        />
      </div>
    </div>
  );
};

export default NewOrderSummary;
